<template>
  <mc-split-view
    :container-class="[
      'answers has-background-white',
      { 'answers--active': open }
    ]"
    :has-right-header="!!selectedAnswerId"
    :height="height"
    :left-flex="1"
    :right-flex="2"
  >
    <template #leftHeader>
      <div class="header-title">
        Shared Answers
      </div>
      <b-button
        outlined
        size="is-small"
        type="is-primary"
        @click="$emit('close')"
      >
        Back To Prompts
      </b-button>
    </template>
    <template #leftBody>
      <div v-if="answerIds.length === 0" class="answers__hint has-text-grey">
        <b-icon icon="information" />
        <div class="content">
          Get started by adding answers
        </div>
      </div>
      <div v-else class="answers__list">
        <div
          v-for="answerId in answerIds"
          :key="answerId"
          :class="[
            'answers__id',
            {
              'answers__id--active': selectedAnswerId === answerId
            }
          ]"
          @click="selectedAnswerId = answerId"
        >
          <div class="answers__id-title">{{ answerId }}</div>
          <a @click.stop="deleteSharedAnswerHandler(answerId)">
            <b-icon icon="delete-outline" type="is-danger" />
          </a>
          <b-icon icon="chevron-right" />
        </div>
      </div>
      <form
        class="answers__add-answer"
        @submit.prevent="addSharedAnswerHandler"
      >
        <b-field v-if="isAddingAnswer">
          <b-input
            v-model="newAnswerId"
            v-bfocus="true"
            expanded
            placeholder="Answer ID"
            type="text"
            icon="message"
          />
          <p class="control">
            <button class="button" native-type="submit">
              <b-icon icon="check" size="is-small" />
            </button>
          </p>
          <p class="control">
            <button class="button" @click="isAddingAnswer = false">
              <b-icon icon="close" size="is-small" />
            </button>
          </p>
        </b-field>
        <b-button
          v-else
          class="is-fullwidth"
          type="is-light"
          @click="isAddingAnswer = true"
        >
          Add Shared Answer
        </b-button>
      </form>
    </template>
    <template v-if="selectedAnswerId" #rightHeader>
      <form class="header-title" @submit.prevent="editIdHandler">
        <b-field v-if="isEditingId">
          <b-input
            v-model="editingAnswerId"
            v-bfocus="true"
            placeholder="Answer ID"
            type="text"
            icon="message"
            size="is-small"
          />
          <p class="control">
            <button class="button is-small" native-type="submit">
              <b-icon icon="check" size="is-small" />
            </button>
          </p>
          <p class="control">
            <button class="button is-small" @click="isEditingId = false">
              <b-icon icon="close" size="is-small" />
            </button>
          </p>
        </b-field>
        <fragment v-else>
          <span>{{ selectedAnswerId }}</span>
          <a class="answers__edit-id" @click="openIdEditor">
            <b-icon icon="pencil" size="is-small" />
          </a>
        </fragment>
      </form>
    </template>
    <template #rightBody>
      <div v-if="selectedAnswerId" class="answers__editor">
        <mc-dnd-list
          v-if="selectedAnswer.length > 0"
          container-class="answers__answers"
          drag-handle-selector=".answer__drag-handle"
          :get-ghost-parent="getGhostParent"
          :list="selectedAnswer"
          :on-drop="moveAnswerHandler"
        >
          <template #item="{ item, index }">
            <answer-editor
              :answer="item"
              :index="index"
              :prompt-keys="promptKeys"
              :on-change="changeAnswerHandler"
              :on-delete="deleteAnswerHandler"
            />
          </template>
        </mc-dnd-list>
        <b-button
          class="is-fullwidth"
          type="is-light"
          @click="addAnswerHandler"
        >
          Add Answer
        </b-button>
      </div>
      <div v-else class="answers__hint has-text-grey">
        <b-icon icon="information" />
        <div class="content">
          Select an answer to start editing
        </div>
      </div>
    </template>
  </mc-split-view>
</template>
<script>
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import forOwn from 'lodash/forOwn';

import AnswerEditor from './AnswerEditor';
import regex from '../../utils/regex';
import { moveByElement, removeByIndex } from '../../utils/array';

export default {
  components: {
    AnswerEditor
  },
  props: {
    definition: {
      type: Object,
      required: true
    },
    height: String,
    onAnswerIdChange: {
      type: Function,
      // eslint-disable-next-line
      default: (oldId, newId) => {/* Update shared answers in prompts */}
    },
    open: Boolean,
    promptKeys: Array
  },
  data: function() {
    return {
      editingAnswerId: false,
      internalDefinition: {
        answers: {},
        prompts: {}
      },
      isAddingAnswer: false,
      isEditingId: false,
      selectedAnswerId: null,
      newAnswerId: ''
    };
  },
  computed: {
    answerIds() {
      return Object.keys(this.answers);
    },
    answers() {
      return this.internalDefinition.answers;
    },
    prompts() {
      return this.internalDefinition.prompts;
    },
    selectedAnswer() {
      if (!this.selectedAnswerId) return null;
      return this.answers[this.selectedAnswerId];
    }
  },
  watch: {
    open: function() {
      this.updateDefinition();
    }
  },
  mounted() {
    this.updateDefinition();
  },
  methods: {
    addAnswerHandler() {
      this.selectedAnswer.push({
        message: '',
        nextKey: ''
      });
      this.triggerOnChange();
    },
    addSharedAnswerHandler() {
      if (!this.isIdValid(this.newAnswerId)) {
        this.$buefy.toast.open({
          message: regex.answerId.message,
          type: 'is-danger'
        });
      } else if (this.doesIdExist(this.newAnswerId)) {
        this.$buefy.toast.open({
          message: 'Answer with same id already exists!',
          type: 'is-danger'
        });
      } else {
        Vue.set(this.answers, this.newAnswerId, []);
        this.newAnswerId = '';
        this.isAddingAnswer = false;
        this.triggerOnChange();
      }
    },
    changeAnswerHandler(data, index) {
      this.selectedAnswer[index] = data;
      this.triggerOnChange();
    },
    deleteAnswerHandler(index) {
      removeByIndex(this.selectedAnswer, index);
      this.triggerOnChange();
    },
    deleteSharedAnswerHandler(id) {
      let isUsedInPrompt = false,
        promptKeys = [];
      forOwn(this.prompts, (v, k) => {
        if (v.answers === id) {
          promptKeys.push(k);
          isUsedInPrompt = true;
        }
      });

      if (isUsedInPrompt) {
        this.$buefy.toast.open(
          `
        This shared answer is being used in the following prompts: ` +
            `${promptKeys.join(
              ', '
            )}. Remove all its usages before deleting it!`
        );
        return;
      }

      this.$buefy.dialog.confirm({
        message: `
        Proceeding will delete this shared answer and can't be undone`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          if (this.selectedAnswerId === id) this.selectedAnswerId = null;
          Vue.delete(this.answers, id);
          this.$buefy.toast.open(
            `Deleted the shared answer '${id}' successfully!`
          );
          this.triggerOnChange();
        }
      });
    },
    doesIdExist(id) {
      return this.answers.hasOwnProperty(id);
    },
    editIdHandler() {
      const oldId = this.selectedAnswerId;
      const newId = this.editingAnswerId;

      if (oldId === newId) {
        this.isEditingId = false;
        return;
      }

      if (!this.isIdValid(newId)) {
        this.$buefy.toast.open({
          message: regex.answerId.message,
          type: 'is-danger'
        });
      } else if (this.doesIdExist(newId)) {
        this.$buefy.toast.open({
          message: 'Answer with same id already exists!',
          type: 'is-danger'
        });
      } else {
        this.udapteSharedAnswerReferences(oldId, newId);
        Vue.set(this.answers, newId, this.answers[oldId]);
        this.selectedAnswerId = newId;
        Vue.delete(this.answers, oldId);
        this.isEditingId = false;
        this.triggerOnChange();
        this.$buefy.toast.open(
          'Answer and all its references successfully updated!'
        );
      }
    },
    getGhostParent() {
      return document.body;
    },
    isIdValid(id) {
      return regex.answerId.pattern.test(id);
    },
    moveAnswerHandler({ fromItem, toItem }) {
      moveByElement(this.selectedAnswer, fromItem, toItem);
      this.triggerOnChange();
    },
    openIdEditor() {
      this.editingAnswerId = this.selectedAnswerId;
      this.isEditingId = true;
    },
    triggerOnChange() {
      this.$emit('change', cloneDeep(this.internalDefinition));
    },
    updateDefinition() {
      if (
        this.selectedAnswerId &&
        !this.definition.answers[this.selectedAnswerId]
      ) {
        this.selectedAnswerId = null;
      }
      this.internalDefinition = cloneDeep(this.definition);
    },
    udapteSharedAnswerReferences(oldId, newId) {
      forOwn(this.prompts, v => {
        if (Array.isArray(v.answers)) return;
        if (v.answers === oldId) v.answers = newId;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.answers {
  z-index: $shared-answers-z;
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  transition: 360ms;
  transform: scaleY(0) translate3d(0, 0, 0);
  opacity: 0;
  transform-origin: bottom;
  will-change: transform, opacity;

  &--active {
    opacity: 1;
    transform: scaleY(1) translate3d(0, 0, 0);
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__answers {
    padding: 0.1rem;
    padding-bottom: 0.5rem;

    & /deep/ .dnd {
      &__draggable {
        padding: 0;
        box-shadow: none;
        overflow: visible !important;
      }
    }
  }

  &__id {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid $border-color;
    cursor: pointer;

    &:hover {
      background-color: rgba($secondary-color, 0.5);
    }

    &--active {
      background-color: $secondary-color !important;
    }
  }

  &__id-title {
    flex: 1 0 0;
    margin-right: 0.5rem;
  }

  &__add-answer {
    padding: 0.5rem;
  }

  &__hint {
    flex: 1 0 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    text-align: center;

    .content {
      margin-left: 0.5rem;
    }
  }

  &__edit-id {
    margin-left: 0.2rem;
  }

  &__editor {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    padding: 0.5rem;
  }
}
</style>
